import { TranslocoDirective, provideTranslocoScope } from '@jsverse/transloco';
import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoCurrencyPipe, TranslocoPercentPipe } from '@jsverse/transloco-locale';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ItemTask } from '../../interfaces/task-approvativo.model';
import { maxDecimals } from '../../utils/task.utils';
import { TaskProductDeleteConfirmDialog } from '../task-product-delete-confirm-dialog/task-product-delete-confirm-dialog';

export interface TaskProductItemEditDialogData {
  item: ItemTask;
}

export interface TaskProductItemEditDialogResult {
  action: 'save' | 'delete';
  item: ItemTask;
}

@Component({
  providers: [provideTranslocoScope('tasks')],
  selector: 'task-product-item-edit-dialog',
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslocoDirective,
    TranslocoCurrencyPipe,
    TranslocoPercentPipe,
  ],
  templateUrl: './task-product-item-edit-dialog.html',
  styleUrl: './task-product-item-edit-dialog.scss',
})
export class TaskProductItemEditDialog {
  readonly item: ItemTask;
  readonly editForm: FormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: TaskProductItemEditDialogData,
    private readonly dialogRef: MatDialogRef<TaskProductItemEditDialog, TaskProductItemEditDialogResult>,
    private readonly dialog: MatDialog,
    formBuilder: FormBuilder,
  ) {
    this.item = { ...data.item };
    this.editForm = formBuilder.nonNullable.group({
      qtaImballi: [this.item.qtaImballi, [Validators.required, Validators.min(0)]],
      prezzo: [
        { value: this.item.prezzo, disabled: !!this.item.prezzoBloccato },
        [Validators.required, Validators.min(0), maxDecimals(4)],
      ],
      pProvvigione: [this.item.pProvvigione ?? 0, [Validators.required, Validators.min(0), maxDecimals(2)]],
      pNoProfit: [this.item.pNoProfit ?? 0, [Validators.required, Validators.min(0), maxDecimals(0)]],
      pFuoriPremio: [this.item.pFuoriPremio ?? 0, [Validators.required, Validators.min(0), maxDecimals(0)]],
    });
  }

  get quantity(): number {
    const quantity = Number(this.editForm.controls['qtaImballi'].value);
    return Number.isNaN(quantity) ? 0 : quantity;
  }

  increaseQuantity(): void {
    this.setQuantity(this.quantity + 1);
  }

  decreaseQuantity(): void {
    this.setQuantity(this.quantity - 1);
  }

  deleteProduct(): void {
    this.dialog
      .open<TaskProductDeleteConfirmDialog, void, boolean>(TaskProductDeleteConfirmDialog, {
        width: 'min(360px, calc(100vw - 32px))',
        maxWidth: '360px',
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.dialogRef.close({
            action: 'delete',
            item: this.item,
          });
        }
      });
  }

  save(): void {
    if (this.editForm.invalid || !this.hasChanges()) return;

    const value = this.editForm.getRawValue();

    this.dialogRef.close({
      action: 'save',
      item: {
        ...this.item,
        ...value,
        pProvvigione: this.percentValue(value, 'pProvvigione'),
        pNoProfit: this.percentValue(value, 'pNoProfit'),
        pFuoriPremio: this.percentValue(value, 'pFuoriPremio'),
      },
    });
  }

  hasChanges(): boolean {
    const value = this.editForm.getRawValue();

    return this.item.qtaImballi !== Number(value.qtaImballi)
      || this.item.prezzo !== Number(value.prezzo)
      || (this.item.pProvvigione ?? 0) !== Number(value.pProvvigione)
      || (this.item.pNoProfit ?? 0) !== Number(value.pNoProfit)
      || (this.item.pFuoriPremio ?? 0) !== Number(value.pFuoriPremio);
  }

  fieldChanged(field: 'prezzo' | 'pProvvigione' | 'pNoProfit' | 'pFuoriPremio'): boolean {
    const value = this.editForm.getRawValue();
    return Number(value[field]) !== Number(this.item[field] ?? 0);
  }

  // Un valore assente si mostra a 0 e resta assente se non cambia
  private percentValue(value: Record<string, number>, field: 'pProvvigione' | 'pNoProfit' | 'pFuoriPremio'): number {
    return (this.item[field] ?? 0) === Number(value[field]) ? this.item[field] : Number(value[field]);
  }

  private setQuantity(quantity: number): void {
    this.editForm.controls['qtaImballi'].setValue(quantity);
  }
}
